import React from "react";
import { User } from "../../model_gen/company";
import { 
  ClockIcon,
  MoreHorizontalIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip } from "../ui/tooltip";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import moment from "moment";

interface PendingUsersTableProps {
  pendingUsers: User[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const PendingUsersTable: React.FC<PendingUsersTableProps> = ({
  pendingUsers,
  loading,
  currentPage,
  pageSize,
  onPageChange,
}) => {
  const formatDate = (timestamp: string) => {
    if (!timestamp) return "Unknown";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<User>[] = [
    {
      key: "email",
      header: "Email",
      width: "w-[50%]",
      render: (user) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
            <ClockIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {user.email}
            </div>
            {user.name && (
              <div className="text-xs text-muted-foreground">
                {user.name}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "created_at",
      header: "Invited",
      width: "w-[25%]",
      render: (user) => (
        user.created_at ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(user.created_at)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(user.created_at)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Unknown</span>
        )
      ),
    },
    {
      key: "pending",
      header: "Status",
      width: "w-[20%]",
      render: () => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
          Pending
        </span>
      ),
    },
    {
      key: "user_id",
      header: "Actions",
      width: "w-[5%]",
      render: () => (
        <Tooltip content="Contact support@trymaitai.com to cancel invitations">
          <Button
            variant="ghost"
            size="sm"
            disabled
            className="gap-1"
          >
            <MoreHorizontalIcon className="w-3 h-3" />
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (pendingUsers.length === 0) {
    return null; // Don't render anything if no pending users
  }

  return (
    <div className="space-y-4">
      <div className="border-t border-border pt-6">
        <h4 className="text-lg font-gc-med text-foreground mb-4">Pending Invitations</h4>
        <PaginatedTable
          data={pendingUsers}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          pageSize={pageSize}
          emptyTitle="No pending invitations"
          emptyDescription="All invitations have been accepted or there are no pending invitations."
          showPagination={false} // Pending users table doesn't need pagination for now
          loadingRows={3}
        />
      </div>
    </div>
  );
};

export default PendingUsersTable; 